import { useEffect, useState } from "react";
import axios from "axios";

/* Holiday greetings + login picture (3-Nov-2026). Shared by the login page,
   the in-app top bar and the admin "Greetings" tab. The server decides which
   greeting is live today (India date) — see /api/public/login-look. */

const API = process.env.REACT_APP_BACKEND_URL;

// Each colour: `hex` for wells and dots, `light` for text on the dark photo
// scrim, `ink` for text on the light top-bar drawer.
export const GREETING_ACCENTS = {
  gold:    { name: "Gold",    hex: "#f59e0b", light: "#fde68a", ink: "#92400e" },
  saffron: { name: "Saffron", hex: "#f97316", light: "#fed7aa", ink: "#9a3412" },
  rose:    { name: "Rose",    hex: "#e11d48", light: "#fecdd3", ink: "#9f1239" },
  blue:    { name: "Blue",    hex: "#3b82f6", light: "#bfdbfe", ink: "#1e40af" },
  green:   { name: "Green",   hex: "#16a34a", light: "#bbf7d0", ink: "#166534" },
};
export const accentOf = (key) => GREETING_ACCENTS[key] || GREETING_ACCENTS.gold;

// Starting points for the admin. Dates are left blank on purpose: most
// festivals move every year, so the admin picks them from the calendar.
export const GREETING_IDEAS = [
  { title: "Diwali", greeting: "Happy Diwali", message: "Wishing you and your family a joyful Diwali and Bandi Chhor Divas. From all of us at Rockpecker.", accent: "gold" },
  { title: "Welcome back", greeting: "Welcome back", message: "Hope you had a wonderful break with your family.", accent: "saffron" },
  { title: "Lohri", greeting: "Happy Lohri", message: "Warm wishes to you and your family. From all of us at Rockpecker.", accent: "saffron" },
  { title: "Republic Day", greeting: "Happy Republic Day", message: "Proud to build, together.", accent: "saffron" },
  { title: "Holi", greeting: "Happy Holi", message: "Wishing you a bright and colourful Holi. From all of us at Rockpecker.", accent: "rose" },
  { title: "Baisakhi", greeting: "Happy Baisakhi", message: "Wishing you a happy harvest season. From all of us at Rockpecker.", accent: "gold" },
  { title: "Eid", greeting: "Eid Mubarak", message: "Warm wishes to you and your family. From all of us at Rockpecker.", accent: "green" },
  { title: "Independence Day", greeting: "Happy Independence Day", message: "Proud to build, together.", accent: "saffron" },
  { title: "Gurpurab", greeting: "Happy Gurpurab", message: "Warm wishes to you and your family. From all of us at Rockpecker.", accent: "gold" },
  { title: "Christmas", greeting: "Merry Christmas", message: "Wishing you and your family a peaceful Christmas.", accent: "rose" },
  { title: "New Year", greeting: "Happy New Year", message: "Thank you for a great year. Here's to the next one.", accent: "blue" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const parse = (ymd) => { const [y, m, d] = ymd.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / 86400000);

// "Fri 6 Nov" — the house date style, with the weekday because the admin is
// planning around working days.
export const formatDay = (ymd, withYear = false) => {
  if (!ymd) return "";
  const d = parse(ymd);
  return `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ""}`;
};
export const formatRange = (start, end) =>
  start === end ? formatDay(start, true) : `${formatDay(start)} – ${formatDay(end, true)}`;

// Where a greeting stands relative to today (India date, from the server).
export const greetingStatus = (o, today) => {
  if (!o || !today) return { key: "none", label: "" };
  if (o.enabled === false) return { key: "off", label: "Switched off" };
  if (today > o.end) return { key: "ended", label: "Ended" };
  if (today >= o.start) {
    const left = daysBetween(today, o.end);
    return { key: "live", label: left === 0 ? "Live · ends tonight" : `Live · ${left + 1} days left` };
  }
  const n = daysBetween(today, o.start);
  return { key: "soon", label: n === 1 ? "Starts tomorrow" : `Starts in ${n} days` };
};

export const mediaUrl = (m) => (m && m.url ? `${API}${m.url}` : "");

// The live greeting + login picture. Remembered in this browser so the login
// page can show the right picture straight away instead of flashing the
// building video first; refreshed from the server on every mount.
const CACHE_KEY = "rl-login-look";
export const LOOK_CHANGED = "rl-login-look-changed";
export function useLoginLook() {
  const [look, setLook] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; }
  });
  const [ready, setReady] = useState(!!look);
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/api/public/login-look`)
      .then(res => {
        if (!alive) return;
        setLook(res.data);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(res.data)); } catch { /* private mode */ }
      })
      .catch(() => {})
      .finally(() => alive && setReady(true));
    load();
    // A tab left open overnight picks up the next day's greeting.
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState === "visible" && Date.now() - last > 30 * 60 * 1000) { last = Date.now(); load(); }
    };
    document.addEventListener("visibilitychange", onVisible);
    // Admin → Greetings fires this after a change, so the top bar updates
    // without a reload.
    window.addEventListener(LOOK_CHANGED, load);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(LOOK_CHANGED, load);
    };
  }, []);
  return { look, ready };
}
